import { FileText, UserPlus, Smartphone, BadgeCheck, Copyright, Scale, Edit3, Mail, Link2, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const SECTIONS = [
  {
    icon: FileText,
    title: "Éditeur et objet",
    body: [
      "Le site chouetteapprentissage.com (le « Site ») et l'application mobile Chouette Apprentissage (l'« Application »), disponibles sur l'App Store et Google Play, sont édités par l'exploitant de la marque Chouette Apprentissage (l'« Éditeur »).",
      "Les présentes Conditions Générales d'Utilisation (« CGU ») encadrent l'accès et l'utilisation du Site et de l'Application par tout utilisateur (« l'Utilisateur »). L'installation de l'Application vaut acceptation sans réserve des présentes CGU.",
    ],
  },
  {
    icon: UserPlus,
    title: "Inscription et compte",
    body: [
      "La création d'un compte élève requiert la fourniture d'informations exactes et à jour. Pour tout élève mineur de moins de 15 ans, le consentement du titulaire de l'autorité parentale est recueilli préalablement à la création du compte.",
      "L'Utilisateur (ou son représentant légal) est responsable de la confidentialité de ses identifiants et de l'usage qui en est fait. Tout compte suspect doit être signalé sans délai via la page Support.",
    ],
  },
  {
    icon: Smartphone,
    title: "Utilisation de l'Application",
    body: [
      "L'Application fournit une aide pédagogique : leçons à lire, exercices, interros notées sur 20 à titre indicatif, fiches de révision et discussions avec le compagnon virtuel.",
      "L'Utilisateur s'engage à utiliser l'Application conformément à sa destination, à ne pas tenter d'en perturber le fonctionnement, d'y introduire du contenu illicite ou de contourner les mesures de sécurité. L'Éditeur met en place une modération afin que les échanges restent bienveillants et sûrs.",
    ],
  },
  {
    icon: BadgeCheck,
    title: "Fonctionnalités, offres et tarifs",
    body: [
      "Les fonctionnalités accessibles gratuitement et celles soumises à abonnement sont présentées directement dans l'Application. Les achats éventuels sont réalisés via l'App Store ou Google Play, dont les conditions de paiement, de renouvellement et de remboursement s'appliquent.",
      "L'Éditeur s'efforce d'assurer la disponibilité continue du service, sous réserve des périodes de maintenance. Le service est fourni sans garantie d'adéquation à un besoin particulier de l'Utilisateur.",
    ],
  },
  {
    icon: Copyright,
    title: "Propriété intellectuelle",
    body: [
      "La marque Chouette Apprentissage, son logo, le compagnon virtuel, ainsi que l'ensemble des contenus du Site et de l'Application (textes, leçons, exercices, illustrations) sont protégés par le droit de la propriété intellectuelle.",
      "Toute reproduction, adaptation ou exploitation non autorisée, totale ou partielle, est interdite sans accord écrit préalable de l'Éditeur.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Données personnelles et mineurs",
    body: [
      "Les données collectées dans le cadre du Site et de l'Application le sont dans la limite du strict nécessaire, sans publicité, et ne sont jamais revendues. Le détail des traitements, des bases légales et des droits des parents est décrit dans notre politique de confidentialité.",
      "L'Éditeur applique les règles de protection des mineurs prévues par le RGPD et la loi Informatique et Libertés.",
    ],
  },
  {
    icon: Edit3,
    title: "Modification des CGU",
    body: [
      "L'Éditeur peut modifier à tout moment les présentes CGU. La version applicable est celle en vigueur et accessible en ligne au jour de l'utilisation du Site ou de l'Application.",
      "Les Utilisateurs seront informés de toute modification substantielle via l'Application ou le Site.",
    ],
  },
  {
    icon: Scale,
    title: "Droit applicable et litiges",
    body: [
      "Les présentes CGU sont soumises au droit français. En cas de litige, les parties s'efforceront de le résoudre à l'amiable ; à défaut, le litige pourra être porté devant les tribunaux compétents, ou traité selon les règles de médiation de la consommation le cas échéant.",
    ],
  },
];

export default function Cgu() {
  return (
    <main className="pt-28 pb-24">
      <div className="max-w-3xl mx-auto px-6">
        <Reveal><span className="eyebrow">CGU</span></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="cgu-title">
            Conditions générales <span className="italic text-coral">d'utilisation</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-ink/65 leading-relaxed" data-testid="cgu-intro">
            L'essentiel en clair : ce que vous pouvez faire avec l'Application et le Site, ce dont nous sommes responsables, et comment tout cela est encadré.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-3 text-xs font-mono uppercase tracking-[0.15em] text-ink/50" data-testid="cgu-updated">
            Dernière mise à jour : janvier 2026
          </p>
        </Reveal>

        <div className="mt-14 space-y-10">
          {SECTIONS.map((s, i) => (
            <Reveal key={i} delay={0.05}>
              <section className="card-paper p-6 sm:p-8" data-testid={`cgu-section-${i}`}>
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-hi text-ink flex items-center justify-center shrink-0">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold" data-testid={`cgu-heading-${i}`}>
                    {i + 1}. {s.title}
                  </h2>
                </div>
                <div className="mt-4 space-y-3">
                  {s.body.map((p, j) => (
                    <p key={j} className="text-sm sm:text-base text-ink/70 leading-relaxed">{p}</p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}

          <Reveal delay={0.05}>
            <section className="rounded-2xl bg-chalk text-paper p-6 sm:p-8" data-testid="cgu-contact">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-paper/10 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-hi" />
                </div>
                <h2 className="font-serif text-xl sm:text-2xl font-bold" data-testid="cgu-contact-title">9. Nous contacter</h2>
              </div>
              <p className="mt-4 text-sm sm:text-base text-paper/80 leading-relaxed">
                Pour toute question relative aux présentes CGU :{" "}
                <a href="mailto:support@chouetteapprentissage.com" className="font-bold text-hi underline" data-testid="cgu-contact-mail">
                  support@chouetteapprentissage.com
                </a>
                , ou via notre <a href="/support" className="font-bold text-hi underline" data-testid="cgu-contact-support">page Support</a>.
              </p>
              <p className="mt-3 text-xs text-paper/50 leading-relaxed">
                <Link2 className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                À lire également : notre{" "}
                <a href="/confidentialite" className="underline text-hi" data-testid="cgu-privacy-link">politique de confidentialité</a>.
              </p>
            </section>
          </Reveal>
        </div>
      </div>
    </main>
  );
}